import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, ShieldCheck, Gauge, Calculator as CalcIcon, Search } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DeviceIcon } from "@/lib/deviceIcons";
import CustomDeviceEstimator from "@/components/CustomDeviceEstimator";
import { getCustomDevices } from "@/lib/api";
import { deviceList, comingSoon } from "@/data/devices";
import { track, trackSettled } from "@/lib/track";

const GENERAL_FAQ = [
  { q: "Wie funktioniert der Rechner?", a: "Du wählst dein Gerät und gibst entweder die Wattzahl ein oder lässt den Verbrauch anhand weniger Fragen schätzen. Du bekommst sofort die Kosten pro Tag, Monat und Jahr als Spanne." },
  { q: "Ist das Tool kostenlos?", a: "Ja, vollständig kostenlos und ohne Anmeldung. Alle Berechnungen laufen direkt in deinem Browser." },
  { q: "Was, wenn ich die Wattzahl nicht kenne?", a: "Dann nutzt du den Modus „Bitte schätzen“. Das Tool rechnet mit typischen Verbrauchsbereichen für dein Gerät und zeigt eine Spanne von niedrig bis hoch." },
  { q: "Wie genau sind die Ergebnisse?", a: "Es sind fundierte Schätzungen auf Basis typischer Verbrauchswerte, keine exakten Messwerte. Für genaue Zahlen hilft ein Strommessgerät." },
  { q: "Welcher Strompreis ist eingestellt?", a: "Voreingestellt ist der offizielle deutsche Endkundenpreis inklusive Steuern (Quelle: Eurostat), der regelmäßig aktualisiert wird. Du kannst deinen eigenen Tarif jederzeit eintragen." },
];

export default function HomePage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [query, setQuery] = useState("");
  const [customDevices, setCustomDevices] = useState([]);
  useEffect(() => {
    getCustomDevices().then(setCustomDevices).catch(() => {});
  }, []);
  const allDevices = [
    ...deviceList,
    ...customDevices.filter((c) => !deviceList.some((d) => d.key === c.key)),
  ];
  const q = query.trim().toLowerCase();
  const filteredDevices = q ? allDevices.filter((d) => d.name.toLowerCase().includes(q)) : allDevices;

  const handleSearchChange = (e) => {
    const raw = e.target.value;
    setQuery(raw);
    const trimmed = raw.trim();
    if (trimmed) {
      const matched = trimmed.toLowerCase();
      const count = allDevices.filter((d) => d.name.toLowerCase().includes(matched)).length;
      trackSettled("search", { query: trimmed, results_count: count });
    }
  };
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Gerätekosten",
      url: origin || undefined,
      inLanguage: "de-DE",
      description: "Stromkosten einzelner Geräte berechnen, auch ohne genaue Wattzahl.",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: GENERAL_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <div>
      <Seo
        title="Stromkosten von Geräten berechnen | Gerätekosten"
        description="Berechne, was einzelne Geräte pro Tag, Monat und Jahr an Strom kosten, auch ohne genaue Wattzahl. Kostenlos, ohne Anmeldung, mit Spanne."
      canonical={origin ? `${origin}/` : undefined}
      />
      <JsonLd data={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-0"
          style={{ background: "linear-gradient(135deg, rgba(15,118,110,0.10), rgba(249,115,22,0.08), rgba(251,250,247,0))", height: "60%" }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-8 relative">
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight max-w-3xl">
            Stromkosten einzelner Geräte berechnen
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Finde heraus, was dein Gerät pro Tag, Monat und Jahr an Strom kostet, auch wenn du den genauen Verbrauch nicht
            kennst. Wähle ein Gerät oder nutze den allgemeinen Rechner.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/stromkostenrechner" data-testid="cta-generic-calculator">
                Allgemeiner Stromkostenrechner <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <a href="#geraete">Gerät auswählen</a>
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Kostenlos, ohne Anmeldung</span>
            <span className="flex items-center gap-2"><CalcIcon className="h-4 w-4 text-primary" /> Auch ohne Wattzahl</span>
            <span className="flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" /> Ergebnis als Spanne</span>
          </div>
        </div>
      </section>

      {/* Geräteauswahl */}
      <section id="geraete" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 scroll-mt-20">
        <h2 className="text-2xl font-semibold tracking-tight mb-1">Gerät auswählen</h2>
        <p className="text-muted-foreground mb-4">Such dein Gerät oder wähle es aus. Jedes hat einen eigenen Rechner mit passender Formel und Beispielrechnung.</p>

        <div className="relative mb-5 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={query}
            onChange={handleSearchChange}
            placeholder="Gerät suchen, z. B. Heizlüfter, Kühlschrank, Trockner …"
            aria-label="Gerät suchen"
            className="pl-9 h-11"
            data-testid="device-search"
          />
        </div>

        {filteredDevices.length === 0 ? (
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-4">
              „{query}“ ist noch nicht als eigene Seite dabei. Lass den Verbrauch direkt per KI schätzen:
            </p>
            <CustomDeviceEstimator initialName={query} />
          </div>
        ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filteredDevices.map((d) => (
            <Link
              key={d.key}
              to={`/${d.slug}`}
              data-testid={`device-card-${d.key}`}
              className="group flex items-center justify-between rounded-2xl border p-5 hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <DeviceIcon name={d.icon} className="h-5 w-5" />
                </span>
                <div>
                  <div className="font-medium">{d.name}</div>
                  <div className="text-xs text-muted-foreground">Stromkosten berechnen</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
            </Link>
          ))}
        </div>
        )}

        {comingSoon.length > 0 && (
          <div className="mt-5 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Bald verfügbar: </span>
            {comingSoon.join(", ")}.
          </div>
        )}

        {filteredDevices.length > 0 && (
          <div className="mt-6">
            <CustomDeviceEstimator />
          </div>
        )}
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6"><AdSlot variant="inline" /></div>

      {/* So funktioniert es */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-2xl font-semibold tracking-tight mb-5">So funktioniert es</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { n: "1", t: "Gerät wählen", d: "Such dir dein Gerät aus oder nutze den allgemeinen Rechner." },
            { n: "2", t: "Verbrauch eingeben oder schätzen", d: "Trage die Wattzahl ein oder beantworte ein paar einfache Fragen." },
            { n: "3", t: "Kosten sehen", d: "Du bekommst die Kosten pro Tag, Monat und Jahr als Spanne, mit sichtbaren Annahmen." },
          ].map((s) => (
            <Card key={s.n} className="p-5">
              <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-semibold">{s.n}</div>
              <div className="mt-3 font-medium">{s.t}</div>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Crosslink */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6">
        <Card className="p-5 border flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm text-muted-foreground flex-1">Verkaufst du ein altes Gerät? Erstelle mit dem Kleinanzeigen-Generator den Anzeigentext.</p>
          <a href="https://kleinanzeigen-generator.de" className="text-sm font-medium hover:underline shrink-0" onClick={() => track("crosslink_click", { target: "kleinanzeigen-generator" })}>Zum Generator</a>
        </Card>
      </div>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-2xl font-semibold tracking-tight mb-4">Häufige Fragen</h2>
        <Faq items={GENERAL_FAQ} />
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6"><AdSlot variant="inline" /></div>
    </div>
  );
}
